import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Send, Trash2, Save, FileText, ChevronRight, Code2 } from "lucide-react";
import { endpoints, type DraftItem, type ValidationIssue } from "@/api/client";
import { MovimentoFormPanel } from "@/components/MovimentoFormPanel";
import { FirDraftEditor } from "@/components/FirDraftEditor";
import {
  buildMovimentoPayload,
  payloadToMovimentoForm,
  type MovimentoFormState,
} from "@/lib/movimentoForm";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input, Label, Textarea } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TBody, TD, TH, THead, TR } from "@/components/ui/table";
import { CenteredLoader, ErrorState, EmptyState } from "@/components/ui/feedback";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { ChoiceSelect } from "@/components/forms";
import { formatDate } from "@/lib/utils";

const DOC_TYPES = [
  { id: "fir", label: "Formulario FIR", description: "Formulario di identificazione rifiuti" },
  { id: "fir_attachment", label: "Allegato FIR", description: "Allegato a un formulario esistente" },
];

const DOC_TYPE_CHOICES = DOC_TYPES.map((t) => ({
  value: t.id,
  label: t.label,
  description: t.description,
}));

export function Drafts() {
  const { id } = useParams();
  const navigate = useNavigate();
  if (id) return <DraftEditor id={id} onClose={() => navigate("/drafts")} />;
  return <DraftList onOpen={(d) => navigate(`/drafts/${d.id}`)} />;
}

function DraftList({ onOpen }: { onOpen: (d: DraftItem) => void }) {
  const qc = useQueryClient();
  const [filter, setFilter] = useState<string>("");
  const list = useQuery({
    queryKey: ["drafts", filter],
    queryFn: () => endpoints.drafts.list(filter ? { doc_type: filter } : {}),
  });
  // I movimenti e i registri sono gestiti direttamente in pagina Registri
  // : qui non li mostriamo per evitare confusione.
  const visibleDrafts = (list.data ?? []).filter(
    (d) => d.doc_type !== "movement" && d.doc_type !== "register",
  );
  const remove = useMutation({
    mutationFn: (i: string) => endpoints.drafts.remove(i),
    onSuccess: () => { toast.success("Bozza eliminata"); qc.invalidateQueries({ queryKey: ["drafts"] }); },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Bozze</h1>
          <p className="text-sm text-muted-foreground">Compila offline e invia a RENTRI con un click</p>
        </div>
        <NewDraftDialog onCreated={(d) => { qc.invalidateQueries({ queryKey: ["drafts"] }); onOpen(d); }} />
      </div>

      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant={filter === "" ? "default" : "outline"} onClick={() => setFilter("")}>Tutte</Button>
        {DOC_TYPES.map((t) => (
          <Button key={t.id} size="sm" variant={filter === t.id ? "default" : "outline"} onClick={() => setFilter(t.id)}>{t.label}</Button>
        ))}
      </div>

      <Card>
        <CardContent className="p-0">
          {list.isLoading && <CenteredLoader />}
          {list.error && <ErrorState error={list.error} />}
          {list.data && visibleDrafts.length === 0 && (
            <EmptyState title="Nessuna bozza" description="Crea una nuova bozza dal pulsante in alto a destra. Le bozze movimento sono gestite direttamente nella pagina Registri." icon={<FileText className="h-8 w-8" />} />
          )}
          {visibleDrafts.length > 0 && (
            <Table>
              <THead><TR><TH>Tipo</TH><TH>Titolo</TH><TH>Parent</TH><TH>Stato</TH><TH>Aggiornato</TH><TH /></TR></THead>
              <TBody>
                {visibleDrafts.map((d: DraftItem) => (
                  <TR key={d.id} className="cursor-pointer" onClick={() => onOpen(d)}>
                    <TD><Badge variant="outline">{d.doc_type}</Badge></TD>
                    <TD className="font-medium">{d.title ?? <span className="text-muted-foreground">—</span>}</TD>
                    <TD className="font-mono text-xs">{d.parent_id ?? "—"}</TD>
                    <TD><DraftStatusBadge status={d.status} /></TD>
                    <TD className="text-xs text-muted-foreground">{formatDate(d.updated_at)}</TD>
                    <TD className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          size="sm"
                          variant="outline"
                          aria-label={`Apri bozza ${d.title ?? d.id}`}
                          onClick={(e) => { e.stopPropagation(); onOpen(d); }}
                        >
                          Apri <ChevronRight className="ml-1 h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          aria-label={`Elimina bozza ${d.title ?? d.id}`}
                          onClick={(e) => { e.stopPropagation(); remove.mutate(d.id); }}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function DraftStatusBadge({ status }: { status: string }) {
  const v =
    status === "sent"
      ? "success"
      : status === "pending"
        ? "warning"
        : status === "sent_failed"
          ? "destructive"
          : status === "ready"
            ? "warning"
            : "secondary";
  return <Badge variant={v as never}>{status}</Badge>;
}

function NewDraftDialog({ onCreated }: { onCreated: (d: DraftItem) => void }) {
  const [open, setOpen] = useState(false);
  const [docType, setDocType] = useState("fir");
  const [parentId, setParentId] = useState("");
  const [title, setTitle] = useState("");

  const create = useMutation({
    mutationFn: () => endpoints.drafts.create({
      doc_type: docType,
      parent_id: parentId || undefined,
      title: title || undefined,
      payload: docType === "fir"
          ? {
              num_iscr_sito: "OP2602VAF081537-TO0001",
              codice_blocco: "NXTWB",
              produttore: { num_iscr_sito: "OP2602VAF081537-TO0001" },
              destinatario: { num_iscr_sito: "OP2602VAF081537-TO0001" },
              trasportatore: { num_iscr_sito: "OP2602VAF081537-TO0001", tipo_trasporto: "Terrestre" },
              rifiuto: {
                eer: "160213",
                caratteristiche_pericolo: ["HP14"],
                quantita: 25,
                unita_misura: "KG",
                stato_fisico: "S",
                provenienza: "S",
                trasporto_adr: true,
              },
            }
          : {},
    }),
    onSuccess: (d) => { toast.success("Bozza creata"); setOpen(false); onCreated(d); },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button><Plus className="mr-2 h-4 w-4" /> Nuova bozza</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Nuova bozza</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Tipo documento</Label>
            <div className="mt-1">
              <ChoiceSelect
                value={docType}
      onChange={(v) => setDocType(v || "fir")}
                options={DOC_TYPE_CHOICES}
                required
                showDescription={false}
              />
            </div>
          </div>
          <div>
            <Label>Titolo (descrittivo)</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Es. Scarico mensile capannone A" />
          </div>
          {docType === "fir" && (
            <div>
              <Label>Unità locale (num_iscr_sito)</Label>
              <Input
                value={parentId}
                onChange={(e) => setParentId(e.target.value)}
                placeholder="OP2602VAF081537-TO0001"
              />
            </div>
          )}
          {docType === "fir_attachment" && (
            <div>
              <Label>Numero FIR</Label>
              <Input value={parentId} onChange={(e) => setParentId(e.target.value)} required />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
          <Button onClick={() => create.mutate()} disabled={create.isPending}>Crea</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DraftEditor({ id, onClose }: { id: string; onClose: () => void }) {
  const draft = useQuery({ queryKey: ["draft", id], queryFn: () => endpoints.drafts.get(id) });

  if (draft.isLoading) return <CenteredLoader />;
  if (draft.error) return <ErrorState error={draft.error} />;
  if (!draft.data) return null;

  if (draft.data.doc_type === "movement") {
    return <MovementDraftEditor draft={draft.data} onClose={onClose} />;
  }

  if (draft.data.doc_type === "fir") {
    return <FirDraftEditor draft={draft.data} onClose={onClose} />;
  }

  return <JsonDraftEditor id={id} draft={draft.data} onClose={onClose} />;
}

function MovementDraftEditor({
  draft,
  onClose,
}: {
  draft: DraftItem;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [form, setForm] = useState<MovimentoFormState | null>(null);
  const [showJson, setShowJson] = useState(false);
  const [jsonText, setJsonText] = useState("");
  const [validation, setValidation] = useState<{
    valid: boolean;
    issues: { field: string; code: string; message: string; severity: string }[];
  } | null>(null);
  const [issues, setIssues] = useState<ValidationIssue[]>([]);

  useEffect(() => {
    const next = payloadToMovimentoForm(draft.payload);
    setForm(next);
    setJsonText(JSON.stringify(draft.payload, null, 2));
    setValidation(null);
    setIssues([]);
  }, [draft.id, draft.updated_at]);

  const save = useMutation({
    mutationFn: async () => {
      if (!form) throw new Error("Form non pronto");
      const payload = await buildMovimentoPayload(form, draft.payload);
      return endpoints.drafts.update(draft.id, {
        payload,
        title: `Movimento ${form.tipo} EER ${form.eer} del ${form.data}`,
      });
    },
    onSuccess: (updated) => {
      toast.success("Salvato");
      setJsonText(JSON.stringify(updated.payload, null, 2));
      qc.invalidateQueries({ queryKey: ["draft", draft.id] });
      qc.invalidateQueries({ queryKey: ["drafts"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const send = useMutation({
    mutationFn: async () => {
      if (!form) throw new Error("Form non pronto");
      const payload = await buildMovimentoPayload(form, draft.payload);
      await endpoints.drafts.update(draft.id, {
        payload,
        title: `Movimento ${form.tipo} EER ${form.eer} del ${form.data}`,
      });
      return endpoints.drafts.send(draft.id);
    },
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["draft", draft.id] });
      qc.invalidateQueries({ queryKey: ["drafts"] });
      if (r.rentri_status >= 400) {
        const body = r.rentri_body;
        const detail =
          typeof body === "object" && body !== null
            ? (body as Record<string, unknown>).title ??
              (body as Record<string, unknown>).detail ??
              JSON.stringify(body).slice(0, 120)
            : String(body ?? "errore");
        toast.error(`RENTRI ha rifiutato (${r.rentri_status})`, {
          description: String(detail),
        });
      } else if (r.rentri_status === 202) {
        toast.success("Accettato da RENTRI (in elaborazione)", {
          description: "Controlla Job RENTRI per l'esito finale.",
        });
      } else {
        toast.success(`Inviato a RENTRI (${r.rentri_status})`);
      }
    },
    onError: (e: Error) => toast.error(`Invio fallito: ${e.message}`),
  });

  const validate = async () => {
    if (!form) return;
    try {
      const payload = await buildMovimentoPayload(form, draft.payload);
      const r = await endpoints.validate.movimento(payload);
      setValidation(r);
      setIssues(r.issues);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Validazione fallita");
    }
  };

  if (!form) return <CenteredLoader />;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            {draft.title ?? `Bozza movimento`}
          </h1>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
            <Badge variant="outline">movement</Badge>
            <DraftStatusBadge status={draft.status} />
            {draft.parent_id && (
              <span>
                Registro: <span className="font-mono">{draft.parent_id}</span>
              </span>
            )}
          </div>
          </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={onClose}>
            Chiudi
          </Button>
          <Button variant="outline" onClick={() => setShowJson((v) => !v)}>
            <Code2 className="mr-2 h-4 w-4" />
            {showJson ? "Nascondi JSON" : "JSON avanzato"}
          </Button>
          <Button variant="outline" onClick={validate}>
            Valida
          </Button>
          <Button variant="outline" onClick={() => save.mutate()} disabled={save.isPending}>
            <Save className="mr-2 h-4 w-4" /> Salva
          </Button>
          <Button
            onClick={() => send.mutate()}
            disabled={draft.status === "sent" || send.isPending}
          >
            <Send className="mr-2 h-4 w-4" /> Invia a RENTRI
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Movimento carico/scarico</CardTitle>
        </CardHeader>
        <CardContent>
          <MovimentoFormPanel form={form} onChange={setForm} issues={issues} />
        </CardContent>
      </Card>

      {validation && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Validazione</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="mb-2">
              {validation.valid ? (
                <Badge variant="success">Valido</Badge>
              ) : (
                <Badge variant="destructive">Non valido</Badge>
              )}
            </div>
            {validation.issues.length === 0 && (
              <p className="text-sm text-muted-foreground">Nessun problema rilevato.</p>
            )}
            <ul className="space-y-1 text-sm">
              {validation.issues.map((i, idx) => (
                <li key={idx} className="flex items-start gap-2">
                  <Badge variant={i.severity === "error" ? "destructive" : "warning"}>
                    {i.severity}
                  </Badge>
                  <span className="font-mono text-xs text-muted-foreground">{i.field}</span>
                  <span>{i.message}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {showJson && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Payload (JSON avanzato)</CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              className="min-h-[280px] font-mono text-xs"
              value={jsonText}
              readOnly
              spellCheck={false}
            />
            <p className="mt-2 text-xs text-muted-foreground">
              Modifica i campi dal form sopra; il JSON si aggiorna al salvataggio.
            </p>
          </CardContent>
        </Card>
      )}

      {draft.rentri_response != null && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Risposta RENTRI</CardTitle>
          </CardHeader>
          <CardContent>
            <pre className="overflow-x-auto rounded bg-muted/50 p-3 text-xs">
              {JSON.stringify(draft.rentri_response, null, 2)}
            </pre>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function JsonDraftEditor({
  id,
  draft,
  onClose,
}: {
  id: string;
  draft: DraftItem;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [text, setText] = useState<string>("");
  const [validation, setValidation] = useState<{ valid: boolean; issues: { field: string; code: string; message: string; severity: string }[] } | null>(null);

  useEffect(() => {
    setText(JSON.stringify(draft.payload, null, 2));
  }, [draft.id, draft.updated_at]);

  const save = useMutation({
    mutationFn: (b: { payload: unknown; status?: string }) => endpoints.drafts.update(id, b),
    onSuccess: () => { toast.success("Salvato"); qc.invalidateQueries({ queryKey: ["draft", id] }); },
    onError: (e: Error) => toast.error(e.message),
  });

  const send = useMutation({
    mutationFn: () => endpoints.drafts.send(id),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["draft", id] });
      qc.invalidateQueries({ queryKey: ["drafts"] });
      if (r.rentri_status >= 400) {
        const body = r.rentri_body;
        const detail = typeof body === "object" && body !== null
          ? (body as Record<string, unknown>).title ??
            (body as Record<string, unknown>).detail ??
            JSON.stringify(body).slice(0, 120)
          : String(body ?? "errore");
        toast.error(`RENTRI ha rifiutato (${r.rentri_status})`, { description: String(detail) });
      } else if (r.rentri_status === 202) {
        toast.success("Accettato da RENTRI (in elaborazione)", {
          description: "Controlla Job RENTRI per l'esito finale.",
        });
      } else {
        toast.success(`Inviato a RENTRI (${r.rentri_status})`);
      }
    },
    onError: (e: Error) => toast.error(`Invio fallito: ${e.message}`),
  });

  const validate = async () => {
    try {
      const parsed = JSON.parse(text);
      const docType = draft.doc_type;
      const r = docType === "movement" ? await endpoints.validate.movimento(parsed)
        : docType === "fir" ? await endpoints.validate.fir(parsed)
        : docType === "register" ? await endpoints.validate.registroApertura(parsed)
        : { valid: true, issues: [] };
      setValidation(r);
    } catch (e) {
      toast.error(`JSON non valido: ${(e as Error).message}`);
    }
  };

  const d = draft;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{d.title ?? `Bozza ${id.slice(0, 8)}`}</h1>
          <div className="mt-1 flex items-center gap-2 text-sm text-muted-foreground">
            <Badge variant="outline">{d.doc_type}</Badge>
            <DraftStatusBadge status={d.status} />
            {d.parent_id && <span>Parent: <span className="font-mono">{d.parent_id}</span></span>}
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onClose}>Chiudi</Button>
          <Button variant="outline" onClick={validate}>Valida</Button>
          <Button
            variant="outline"
            onClick={() => {
              try {
                save.mutate({ payload: JSON.parse(text || "null") });
              } catch (e) {
                toast.error(`JSON non valido: ${(e as Error).message}`);
              }
            }}
          >
            <Save className="mr-2 h-4 w-4" /> Salva
          </Button>
          <Button
            onClick={() => send.mutate()}
            disabled={d.status === "sent" || send.isPending}
          >
            <Send className="mr-2 h-4 w-4" /> Invia a RENTRI
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">Payload (JSON)</CardTitle></CardHeader>
        <CardContent>
          <Textarea
            className="min-h-[420px] font-mono text-xs"
            value={text}
            onChange={(e) => setText(e.target.value)}
            spellCheck={false}
          />
        </CardContent>
      </Card>

      {validation && (
        <Card>
          <CardHeader><CardTitle className="text-base">Validazione</CardTitle></CardHeader>
          <CardContent>
            <div className="mb-2">
              {validation.valid
                ? <Badge variant="success">Valido</Badge>
                : <Badge variant="destructive">Non valido</Badge>}
            </div>
            {validation.issues.length === 0 && <p className="text-sm text-muted-foreground">Nessun problema rilevato.</p>}
            <ul className="space-y-1 text-sm">
              {validation.issues.map((i, idx) => (
                <li key={idx} className="flex items-start gap-2">
                  <Badge variant={i.severity === "error" ? "destructive" : "warning"}>{i.severity}</Badge>
                  <span className="font-mono text-xs text-muted-foreground">{i.field}</span>
                  <span>{i.message}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {d.rentri_response != null && (
        <Card>
          <CardHeader><CardTitle className="text-base">Risposta RENTRI</CardTitle></CardHeader>
          <CardContent>
            <pre className="overflow-x-auto rounded bg-muted/50 p-3 text-xs">{JSON.stringify(d.rentri_response, null, 2)}</pre>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
